// Mesh turntable (2026-09-28, shared 2026-09-30): a Fab-style product viewer for ONE GLB — the cosmetics product page
// (cosmetic.php) and the item mesh gallery (object-card/mesh-gallery.ts). Drag to orbit, wheel / pinch to zoom,
// double-click to reset; it auto-spins until touched and resumes after a pause. Lighting is the mesh-thumb studio rig
// (demos/ft3d/mesh-thumb/thumb.js), so the live model matches the thumbnail it was baked from.
//
// three + GLTFLoader come from the vendored ESM build through the site-wide import map (template_page_header.php),
// loaded lazily so a page never pays for WebGL until a viewer mounts.
//
// SIGNED-IN ONLY: the servers hand a GLB url to signed-in viewers alone (mesh_thumbnails_inc.php,
// ft_mesh_skin_store_products) — a signed-out viewer gets the baked thumbnail and a sign-in prompt.

import { buildEmptyState } from './empty-state';
import { WEBGL_UNAVAILABLE_LABEL, WEBGL_UNAVAILABLE_TITLE, WEBGL_UNAVAILABLE_TEXT } from './webgl-availability';

const THREE_URL = '/ft3d/vendor/three/three.module.min.js';
const GLTF_LOADER_URL = '/ft3d/vendor/three/jsm/loaders/GLTFLoader.js';
const IDLE_RESUME_MS = 4000;
const AUTO_SPIN_RAD_PER_S = 0.45;

type Three = any;

export interface TurntableModel {
	url: string;
	sha256: string;
	/** Which end of a long weapon goes up: 'wide' = the head of a haft (mace, axe, spear), 'narrow' = a blade's tip. */
	headUp?: 'wide' | 'narrow' | 'none';
}

export interface TurntableHandle {
	dispose(): void;
}

/** Worn / placed things face front; a long weapon stands head-up (a blade tip-up, a haft head-up). */
export function turntableHeadUp(category: string, name: string): NonNullable<TurntableModel['headUp']> {
	if (category !== 'weapon') return 'none';
	return /sword|scimitar|dagger|knife|blade|katana|rapier|sabre|saber/i.test(name) ? 'narrow' : 'wide';
}

function importModule(url: string): Promise<any> {
	return import(/* @vite-ignore */ url);
}

async function fetchVerified(model: TurntableModel, onProgress: (fraction: number) => void): Promise<ArrayBuffer> {
	const response = await fetch(model.url, { mode: 'cors', credentials: 'omit' });
	if (!response.ok) throw new Error(`Model download failed (${response.status}).`);
	const total = Number(response.headers.get('content-length') || 0);
	let buffer: ArrayBuffer;
	if (response.body && total > 0) {
		const reader = response.body.getReader();
		const chunks: Uint8Array[] = [];
		let received = 0;
		for (;;) {
			const { done, value } = await reader.read();
			if (done) break;
			chunks.push(value);
			received += value.byteLength;
			onProgress(Math.min(1, received / total));
		}
		const joined = new Uint8Array(received);
		let offset = 0;
		for (const chunk of chunks) {
			joined.set(chunk, offset);
			offset += chunk.byteLength;
		}
		buffer = joined.buffer;
	} else {
		buffer = await response.arrayBuffer();
	}
	onProgress(1);
	if (/^[a-f0-9]{64}$/.test(model.sha256) && crypto?.subtle) {
		const digest = new Uint8Array(await crypto.subtle.digest('SHA-256', buffer));
		const hex = Array.from(digest, (b) => b.toString(16).padStart(2, '0')).join('');
		if (hex !== model.sha256) throw new Error('Model failed its integrity check.');
	}
	return buffer;
}

/**
 * Stand a long, thin weapon upright so the turntable spins it about its length instead of sweeping it, then put
 * the right END up. The catalogue's GLBs are authored in mixed orientations (some hang head-down from the grip), so
 * the end is read from the shape: the end slice with the wider cross-section is a haft's head, the narrower one a
 * blade's tip.
 */
function standUpright(THREE: Three, root: any, headUp: TurntableModel['headUp']): void {
	root.updateMatrixWorld(true);
	const size = new THREE.Box3().setFromObject(root).getSize(new THREE.Vector3());
	const longest = Math.max(size.x, size.y, size.z);
	const others = [size.x, size.y, size.z].sort((a, b) => a - b);
	if (longest / Math.max(1e-6, others[1] ?? 0) < 1.8) return; // not a long, thin thing (armor, claws)
	// World-space turns (premultiply), so they compose regardless of the root's own rotation.
	const turn = (x: number, y: number, z: number, angle: number) => {
		root.quaternion.premultiply(new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(x, y, z), angle));
		root.updateMatrixWorld(true);
	};
	if (size.x === longest) turn(0, 0, 1, Math.PI / 2);
	else if (size.z === longest) turn(1, 0, 0, Math.PI / 2);
	if (!headUp || headUp === 'none') return;

	const box = new THREE.Box3().setFromObject(root);
	// A hand-held weapon's origin is its grip, so the far end is the head / tip. Trust it whenever the origin sits
	// clearly toward one end; only a mid-shaft grip (a spear) falls through to the shape reading below.
	const grip = new THREE.Vector3(0, 0, 0).applyMatrix4(root.matrixWorld);
	const along = (grip.y - box.min.y) / Math.max(1e-6, box.max.y - box.min.y);
	if (along >= -0.05 && along <= 1.05 && Math.abs(along - 0.5) > 0.15) {
		if (along > 0.5) turn(0, 0, 1, Math.PI);
		return;
	}

	const cx = (box.min.x + box.max.x) / 2;
	const cz = (box.min.z + box.max.z) / 2;
	const band = (box.max.y - box.min.y) * 0.2;
	let top = 0;
	let bottom = 0;
	const v = new THREE.Vector3();
	root.traverse((node: any) => {
		const pos = node.isMesh ? node.geometry?.attributes?.position : null;
		if (!pos) return;
		const stride = Math.max(1, Math.floor(pos.count / 20000)); // sample huge meshes
		for (let i = 0; i < pos.count; i += stride) {
			v.fromBufferAttribute(pos, i).applyMatrix4(node.matrixWorld);
			const r = Math.hypot(v.x - cx, v.z - cz);
			if (v.y > box.max.y - band) top = Math.max(top, r);
			else if (v.y < box.min.y + band) bottom = Math.max(bottom, r);
		}
	});
	const wideIsUp = top >= bottom;
	if ((headUp === 'wide') !== wideIsUp) {
		turn(0, 0, 1, Math.PI);
	}
}

export async function mountTurntable(
	host: HTMLElement,
	model: TurntableModel,
	status: (state: 'loading' | 'ready' | 'error', detail?: string | number) => void,
): Promise<TurntableHandle> {
	let disposed = false;
	let frame = 0;
	const cleanups: Array<() => void> = [];
	const handle: TurntableHandle = {
		dispose() {
			disposed = true;
			cancelAnimationFrame(frame);
			cleanups.splice(0).forEach((fn) => fn());
		},
	};

	status('loading', 0);
	let THREE: Three;
	let GLTFLoader: any;
	let buffer: ArrayBuffer;
	try {
		[THREE, { GLTFLoader }, buffer] = await Promise.all([
			importModule(THREE_URL),
			importModule(GLTF_LOADER_URL),
			fetchVerified(model, (f) => status('loading', f)),
		]);
	} catch (error) {
		status('error', error instanceof Error ? error.message : 'The 3D preview could not load.');
		return handle;
	}
	if (disposed) return handle;

	let gltf: any;
	try {
		// Resource base = the model's own directory, so a .gltf's relative buffers/textures resolve on the CDN.
		gltf = await new GLTFLoader().parseAsync(buffer, model.url.slice(0, model.url.lastIndexOf('/') + 1));
	} catch {
		status('error', 'The 3D preview could not be read.');
		return handle;
	}
	if (disposed) return handle;

	let renderer: any;
	try {
		renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: 'high-performance' });
	} catch {
		status('error', WEBGL_UNAVAILABLE_TEXT);
		const empty = buildEmptyState({ art: 'orb', label: WEBGL_UNAVAILABLE_LABEL, title: WEBGL_UNAVAILABLE_TITLE, text: WEBGL_UNAVAILABLE_TEXT, compact: true });
		const notice = host.querySelector<HTMLElement>('[data-ft-viewer-error]');
		if (notice) notice.replaceChildren(empty);
		else host.append(empty);
		cleanups.push(() => empty.remove());
		return handle;
	}
	renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
	renderer.outputColorSpace = THREE.SRGBColorSpace;
	renderer.toneMapping = THREE.ACESFilmicToneMapping;
	renderer.toneMappingExposure = 1.05;
	renderer.domElement.className = 'ft-cosmetic-viewer__canvas';
	host.appendChild(renderer.domElement);
	cleanups.push(() => {
		renderer.dispose();
		renderer.domElement.remove();
	});

	const scene = new THREE.Scene();
	scene.add(new THREE.HemisphereLight(0xffffff, 0x404050, 1.15));
	const key = new THREE.DirectionalLight(0xfff2e0, 2.2);
	key.position.set(3, 4.5, 4);
	const fill = new THREE.DirectionalLight(0xdfe8ff, 0.85);
	fill.position.set(-4, 1.5, 2.5);
	const rim = new THREE.DirectionalLight(0xffffff, 1.1);
	rim.position.set(-1.5, 3, -4);
	// Lights ride with the camera so the lit side always faces the viewer, as in a product booth.
	const camera = new THREE.PerspectiveCamera(32, 1, 0.01, 1000);
	camera.add(key, fill, rim);
	scene.add(camera);

	const root = gltf.scene || gltf.scenes?.[0];
	if (!root) {
		status('error', 'The 3D preview has no model.');
		handle.dispose();
		return handle;
	}
	root.traverse((node: any) => {
		if (node.isMesh || node.isSkinnedMesh) node.frustumCulled = false; // bind-pose skinned bounds are unreliable
	});
	// Free the model's GPU resources on dispose (a body switch loads a whole new model); shared ones once.
	cleanups.push(() => {
		const seen = new Set<any>();
		const free = (resource: any) => {
			if (resource && !seen.has(resource) && typeof resource.dispose === 'function') {
				seen.add(resource);
				resource.dispose();
			}
		};
		root.traverse((node: any) => {
			free(node.geometry);
			free(node.skeleton);
			for (const material of Array.isArray(node.material) ? node.material : [node.material]) {
				if (!material) continue;
				for (const value of Object.values(material)) {
					if ((value as any)?.isTexture) {
						(value as any).source?.data?.close?.(); // ImageBitmap
						free(value);
					}
				}
				free(material);
			}
		});
	});
	standUpright(THREE, root, model.headUp);
	const pivot = new THREE.Group();
	pivot.add(root);
	scene.add(pivot);
	const box = new THREE.Box3().setFromObject(pivot);
	if (box.isEmpty() || !Number.isFinite(box.min.x)) {
		status('error', 'The 3D preview has no visible geometry.');
		handle.dispose();
		return handle;
	}
	const center = box.getCenter(new THREE.Vector3());
	root.position.sub(center); // spin about the model's own centre
	const size = box.getSize(new THREE.Vector3());
	const halfHeight = size.y / 2;
	// A weapon faces its broad side (an axe's blade, not its edge). Armor arrives as its standard-body fitting, which
	// faces glTF +Z like the body it was fitted to, so it takes the front view.
	const broadside = model.headUp === 'none' || size.x >= size.z ? 0 : Math.PI / 2;
	const radialXZ = Math.hypot(size.x, size.z) / 2;

	// Orbit state (spherical about the origin).
	const home = { theta: broadside + Math.PI / 9, phi: 0.12, dist: 1 };
	const orbit = { ...home };
	let minDist = 0.1;
	let maxDist = 10;

	const fit = () => {
		const width = Math.max(1, host.clientWidth);
		const height = Math.max(1, host.clientHeight);
		renderer.setSize(width, height, false);
		camera.aspect = width / height;
		const tanV = Math.tan(THREE.MathUtils.degToRad(camera.fov / 2));
		const tanH = tanV * camera.aspect;
		const dist = Math.max(halfHeight / tanV, radialXZ / tanH) * 1.12 + radialXZ;
		const firstFit = home.dist === 1;
		home.dist = dist;
		if (firstFit) orbit.dist = dist;
		minDist = dist * 0.3;
		maxDist = dist * 2.4;
		orbit.dist = Math.min(maxDist, Math.max(minDist, orbit.dist));
		camera.near = Math.max(0.001, dist / 200);
		camera.far = dist * 20;
		camera.updateProjectionMatrix();
	};
	fit();
	const resizeObserver = new ResizeObserver(fit);
	resizeObserver.observe(host);
	cleanups.push(() => resizeObserver.disconnect());

	// Input: pointer drag orbits, wheel / pinch zooms, double-click resets.
	let lastInteraction = -Infinity;
	const pointers = new Map<number, { x: number; y: number }>();
	let pinchStart = 0;
	let pinchDist = 0;
	const canvas = renderer.domElement;
	canvas.style.touchAction = 'none';
	const touch = () => { lastInteraction = performance.now(); };
	const pinchSpan = (): number => {
		const [a, b] = [...pointers.values()];
		return a && b ? Math.hypot(a.x - b.x, a.y - b.y) : 0;
	};
	const onDown = (e: PointerEvent) => {
		canvas.setPointerCapture(e.pointerId);
		pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
		if (pointers.size === 2) {
			pinchStart = pinchSpan();
			pinchDist = orbit.dist;
		}
		host.classList.add('is-grabbing');
		touch();
	};
	const onMove = (e: PointerEvent) => {
		const prev = pointers.get(e.pointerId);
		if (!prev) return;
		const next = { x: e.clientX, y: e.clientY };
		pointers.set(e.pointerId, next);
		if (pointers.size === 1) {
			orbit.theta -= (next.x - prev.x) * 0.008;
			orbit.phi = Math.min(1.35, Math.max(-1.35, orbit.phi + (next.y - prev.y) * 0.008));
		} else if (pointers.size === 2 && pinchStart > 0) {
			const span = pinchSpan();
			orbit.dist = Math.min(maxDist, Math.max(minDist, pinchDist * (pinchStart / Math.max(1, span))));
		}
		touch();
	};
	const onUp = (e: PointerEvent) => {
		pointers.delete(e.pointerId);
		if (pointers.size < 2) pinchStart = 0;
		if (pointers.size === 0) host.classList.remove('is-grabbing');
		touch();
	};
	const onWheel = (e: WheelEvent) => {
		e.preventDefault();
		orbit.dist = Math.min(maxDist, Math.max(minDist, orbit.dist * Math.exp(e.deltaY * 0.0012)));
		touch();
	};
	const onReset = () => {
		Object.assign(orbit, home);
		lastInteraction = -Infinity;
	};
	canvas.addEventListener('pointerdown', onDown);
	canvas.addEventListener('pointermove', onMove);
	canvas.addEventListener('pointerup', onUp);
	canvas.addEventListener('pointercancel', onUp);
	canvas.addEventListener('wheel', onWheel, { passive: false });
	canvas.addEventListener('dblclick', onReset);
	cleanups.push(() => {
		canvas.removeEventListener('pointerdown', onDown);
		canvas.removeEventListener('pointermove', onMove);
		canvas.removeEventListener('pointerup', onUp);
		canvas.removeEventListener('pointercancel', onUp);
		canvas.removeEventListener('wheel', onWheel);
		canvas.removeEventListener('dblclick', onReset);
	});

	const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
	let last = performance.now();
	const tick = (now: number) => {
		if (disposed) return;
		const dt = Math.min(0.1, (now - last) / 1000);
		last = now;
		if (!reduceMotion && pointers.size === 0 && now - lastInteraction > IDLE_RESUME_MS) {
			orbit.theta += AUTO_SPIN_RAD_PER_S * dt;
		}
		const cosPhi = Math.cos(orbit.phi);
		camera.position.set(
			orbit.dist * cosPhi * Math.sin(orbit.theta),
			orbit.dist * Math.sin(orbit.phi),
			orbit.dist * cosPhi * Math.cos(orbit.theta),
		);
		camera.lookAt(0, 0, 0);
		renderer.render(scene, camera);
		frame = requestAnimationFrame(tick);
	};
	frame = requestAnimationFrame(tick);
	status('ready');

	return handle;
}
